// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/solid';
import SpinnerLoad from '../../moduleComponents/spinner_load';

// ###### COMPONENT
const ClusterForm = ({ cluster = null, onSave, onCancel, isSubmitting = false }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');

  // ###### INITIALIZE FORM
  useEffect(() => {
    if (cluster) {
      setName(cluster.name || '');
      setColor(cluster.color || '#3B82F6');
    } else {
      setName('');
      setColor('#3B82F6');
    }
  }, [cluster]);

  // ###### HANDLERS
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
  };

  // ###### COLOR PRESETS
  const colorPresets = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // yellow
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
  ];

  // ###### RENDER
  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Name Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Cluster Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
          placeholder="Enter cluster name"
          required
          disabled={isSubmitting}
        />
      </div>

      {/* Color Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Color
        </label>
        <div className="flex items-center gap-3">
          {/* Color Presets */}
          <div className="flex gap-2">
            {colorPresets.map((presetColor) => (
              <button
                key={presetColor}
                type="button"
                onClick={() => setColor(presetColor)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  color === presetColor ? 'border-gray-800 scale-110' : 'border-gray-300'
                }`}
                style={{ backgroundColor: presetColor }}
                disabled={isSubmitting}
              />
            ))}
          </div>
          {/* Custom Color Input */}
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
              disabled={isSubmitting}
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-xs font-mono"
              placeholder="#3B82F6"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !name.trim()}
          className="p-2 rounded-lg transition-colors bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {isSubmitting ? (
            <SpinnerLoad size="small" />
          ) : (
            <CheckIcon className="h-5 w-5" />
          )}
        </button>
      </div>
    </form>
  );
};

export default ClusterForm;
